import React, { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Divider,
  FormControlLabel,
  Grid,
  MenuItem,
  Paper,
  TextField,
  Typography,
} from '@mui/material';
import CourierLayout from '../CourierLayout';
import { API } from '../../../config';

const SettingsAndPreferences = () => {
  const [preferences, setPreferences] = useState({
    emailNotifications: true,
    pushNotifications: true,
    loadUpdates: true,
    bidUpdates: true,
    negotiationUpdates: true,
    payoutUpdates: true,
  });

  const [localization, setLocalization] = useState({
    language: 'English',
    region: 'United States',
    units: 'Miles / Pounds',
    timeZone: 'Mountain Time',
  });

  const [message, setMessage] = useState('');

  const token =
  localStorage.getItem('courier_token') ||
  localStorage.getItem('token');

  const handlePreferenceChange = (field) => {
    setPreferences((prev) => ({
      ...prev,
      [field]: !prev[field],
    }));
  };

  const handleLocalizationChange = (field, value) => {
    setLocalization((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const savePreferences = () => {
    setMessage('Notification preferences saved.');
  };

  const saveLocalization = () => {
    setMessage('Language and localization settings saved.');
  };

 const requestAccountDeletion = async () => {
  try {
    setMessage('');

    const response = await fetch(`${API}/api/courier/support-request`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        location: '',
        issue: 'Account deletion request',
        contact: 'Contact user through account email on file.',
        category: 'account_deletion',
      }),
    });

    if (!response.ok) {
      throw new Error('Unable to submit account deletion request.');
    }

    setMessage('Account deletion request received. Support will review your request.');
  } catch (error) {
    setMessage(error.message || 'Unable to submit account deletion request.');
  }
};

  return (
    <CourierLayout>
      <Box sx={{ mb: 3 }}>
        <Typography
          variant="h4"
          sx={{
            fontSize: '2rem',
            fontWeight: 700,
            lineHeight: 1.25,
            color: '#0f172a',
            mb: 1,
            textAlign: 'center',
          }}
        >
          Settings
        </Typography>

        <Typography
          variant="body2"
          sx={{
            color: '#64748b',
            textAlign: 'center',
            fontSize: '0.95rem',
            lineHeight: 1.5,
          }}
        >
          Manage notifications, language, localization, and account options.
        </Typography>
      </Box>

      {message && (
        <Alert
          severity="success"
          sx={{
            mb: 2,
            borderRadius: 2,
          }}
        >
          {message}
        </Alert>
      )}

      <Paper
        elevation={1}
        sx={{
          p: { xs: 2, md: 2.5 },
          borderRadius: 3,
          border: '1px solid rgba(15, 23, 42, 0.08)',
          backgroundColor: '#ffffff',
          mb: 2,
        }}
      >
        <Typography
          variant="subtitle1"
          sx={{
            color: '#0f172a',
            fontWeight: 700,
            mb: 0.75,
          }}
        >
          Notification Preferences
        </Typography>

        <Typography
          variant="body2"
          sx={{
            color: '#64748b',
            fontSize: '0.95rem',
            mb: 2,
          }}
        >
          Choose which operational updates you want to receive.
        </Typography>

        <Divider sx={{ mb: 2 }} />

        <Grid container spacing={1}>
          <Grid item xs={12} md={6}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={preferences.emailNotifications}
                  onChange={() => handlePreferenceChange('emailNotifications')}
                />
              }
              label="Email notifications"
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={preferences.pushNotifications}
                  onChange={() => handlePreferenceChange('pushNotifications')}
                />
              }
              label="Push notifications"
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={preferences.loadUpdates}
                  onChange={() => handlePreferenceChange('loadUpdates')}
                />
              }
              label="Load updates"
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={preferences.bidUpdates}
                  onChange={() => handlePreferenceChange('bidUpdates')}
                />
              }
              label="Bid updates"
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={preferences.negotiationUpdates}
                  onChange={() =>
                    handlePreferenceChange('negotiationUpdates')
                  }
                />
              }
              label="Negotiation updates"
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={preferences.payoutUpdates}
                  onChange={() => handlePreferenceChange('payoutUpdates')}
                />
              }
              label="Payout updates"
            />
          </Grid>

          <Grid item xs={12}>
            <Box
              sx={{
                display: 'flex',
                justifyContent: { xs: 'stretch', sm: 'flex-end' },
                mt: 1,
              }}
            >
              <Button
                variant="contained"
                color="primary"
                onClick={savePreferences}
                sx={{
                  minHeight: 42,
                  px: 4,
                  textTransform: 'none',
                  fontWeight: 700,
                  borderRadius: 2,
                  width: { xs: '100%', sm: 'auto' },
                }}
              >
                Save Notifications
              </Button>
            </Box>
          </Grid>
        </Grid>
      </Paper>

      <Paper
        elevation={1}
        sx={{
          p: { xs: 2, md: 2.5 },
          borderRadius: 3,
          border: '1px solid rgba(15, 23, 42, 0.08)',
          backgroundColor: '#ffffff',
          mb: 2,
        }}
      >
        <Typography
          variant="subtitle1"
          sx={{
            color: '#0f172a',
            fontWeight: 700,
            mb: 0.75,
          }}
        >
          Language & Localization
        </Typography>

        <Typography
          variant="body2"
          sx={{
            color: '#64748b',
            fontSize: '0.95rem',
            mb: 2,
          }}
        >
          Set language, region, units, and time zone preferences.
        </Typography>

        <Divider sx={{ mb: 2 }} />

        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <TextField
              select
              label="Language"
              fullWidth
              value={localization.language}
              onChange={(event) =>
                handleLocalizationChange('language', event.target.value)
              }
            >
              <MenuItem value="English">English</MenuItem>
              <MenuItem value="Spanish">Spanish</MenuItem>
            </TextField>
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              select
              label="Region"
              fullWidth
              value={localization.region}
              onChange={(event) =>
                handleLocalizationChange('region', event.target.value)
              }
            >
              <MenuItem value="United States">United States</MenuItem>
              <MenuItem value="Canada">Canada</MenuItem>
            </TextField>
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              select
              label="Units"
              fullWidth
              value={localization.units}
              onChange={(event) =>
                handleLocalizationChange('units', event.target.value)
              }
            >
              <MenuItem value="Miles / Pounds">Miles / Pounds</MenuItem>
              <MenuItem value="Kilometers / Kilograms">
                Kilometers / Kilograms
              </MenuItem>
            </TextField>
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              select
              label="Time Zone"
              fullWidth
              value={localization.timeZone}
              onChange={(event) =>
                handleLocalizationChange('timeZone', event.target.value)
              }
            >
              <MenuItem value="Eastern Time">Eastern Time</MenuItem>
              <MenuItem value="Central Time">Central Time</MenuItem>
              <MenuItem value="Mountain Time">Mountain Time</MenuItem>
              <MenuItem value="Pacific Time">Pacific Time</MenuItem>
            </TextField>
          </Grid>

          <Grid item xs={12}>
            <Box
              sx={{
                display: 'flex',
                justifyContent: { xs: 'stretch', sm: 'flex-end' },
                mt: 1,
              }}
            >
              <Button
                variant="contained"
                color="primary"
                onClick={saveLocalization}
                sx={{
                  minHeight: 42,
                  px: 4,
                  textTransform: 'none',
                  fontWeight: 700,
                  borderRadius: 2,
                  width: { xs: '100%', sm: 'auto' },
                }}
              >
                Save Localization
              </Button>
            </Box>
          </Grid>
        </Grid>
      </Paper>

      <Paper
        elevation={1}
        sx={{
          p: { xs: 2, md: 2.5 },
          borderRadius: 3,
          border: '1px solid rgba(185, 28, 28, 0.18)',
          backgroundColor: '#ffffff',
        }}
      >
        <Typography
          variant="subtitle1"
          sx={{
            color: '#991b1b',
            fontWeight: 700,
            mb: 0.75,
          }}
        >
          Account Deletion
        </Typography>

        <Typography
          variant="body2"
          sx={{
            color: '#64748b',
            fontSize: '0.95rem',
            lineHeight: 1.5,
            mb: 2,
          }}
        >
          You may request deletion of your driver or carrier account. Some records
          may be retained where required for legal, tax, payment, fraud prevention,
          safety, dispute, or compliance purposes.
        </Typography>

        <Button
          variant="outlined"
          color="error"
          onClick={requestAccountDeletion}
          sx={{
            minHeight: 42,
            px: 4,
            textTransform: 'none',
            fontWeight: 700,
            borderRadius: 2,
            width: { xs: '100%', sm: 'auto' },
          }}
        >
          Request Account Deletion
        </Button>
      </Paper>
    </CourierLayout>
  );
};

export default SettingsAndPreferences;